// src/components/admin/AdminUsers.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../service/api_elcristo';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at?: string;
}

interface AdminUsersProps {
  currentUser: any;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'editor',
    password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getUsers();
      // Convertir objeto a array
      const usersArray = Object.values(data);
      setUsers(usersArray as User[]);
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ type: 'error', text: 'Error al cargar usuarios' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        password: ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        role: 'editor',
        password: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      role: 'editor',
      password: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      if (editingUser) {
        // Actualizar usuario
        await apiElCristo.updateUser(editingUser.id, formData);
        setMessage({ type: 'success', text: '✅ Usuario actualizado correctamente' });
      } else {
        // Crear usuario
        if (!formData.password) {
          setMessage({ type: 'error', text: '❌ La contraseña es requerida para nuevos usuarios' });
          return;
        }
        await apiElCristo.createUser(formData);
        setMessage({ type: 'success', text: '✅ Usuario creado correctamente' });
      }

      handleCloseModal();
      loadUsers();

      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving user:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    }
  };

  const handleDelete = async (user: User) => {
    if (user.id === currentUser?.id) {
      setMessage({ type: 'error', text: '❌ No puedes eliminar tu propio usuario' });
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${user.name}?`)) {
      return;
    }

    try {
      await apiElCristo.deleteUser(user.id);
      setMessage({ type: 'success', text: '✅ Usuario eliminado correctamente' });
      loadUsers();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setMessage({ type: 'error', text: '❌ Error al eliminar: ' + error.message });
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div className="content-header">
        <div>
          <h1><i className="fas fa-user-shield"></i> Gestión de Usuarios</h1>
          <p>Administrar accesos al panel</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nuevo Usuario
        </button>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Users Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Creado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-small">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`badge badge-${user.role}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  {user.created_at 
                    ? new Date(user.created_at).toLocaleDateString('es-CL')
                    : 'N/A'
                  }
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleOpenModal(user)}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(user)}
                        title="Eliminar"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-users"></i>
            <p>No hay usuarios registrados</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-user"></i>
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="name">Nombre Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="usuario@colegioelcristo.cl"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Rol *</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="admin">Administrador</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Visor</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    Contraseña {editingUser ? '(dejar vacío para no cambiar)' : '*'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  <i className="fas fa-times"></i>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  <i className="fas fa-save"></i>
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
